import { FC, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import cn from 'classnames';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  AddToProjectModalProvider,
  HoldsAndCheckoutsMessage,
  PageTitle,
} from '@app/components';
import { useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { HoldsAndCheckoutsTab, holdsAndCheckoutsTabs } from '@app/constants';

import { TabsWithRouting } from '../TabsWithRouting';
import { HoldsAndCheckoutsTabContent } from './HoldsAndCheckoutsTabContent';
import { getTabs } from './constants';

import './HoldsAndCheckoutsPage.scss';

const cnBem = createBemBlockBuilder(['holdsAndCheckoutsPage']);

const tabsTypes = Object.keys(holdsAndCheckoutsTabs) as HoldsAndCheckoutsTab[];

export const HoldsAndCheckoutsPage: FC = () => {
  const { $t } = useIntl();
  const { holdsAndCheckoutsRoute, selectConfiguredOption, getTabRoute } =
    useHoldsAndCheckoutsPageConfig();

  const [tabsCounters, setTabsCounters] = useState(
    Object.fromEntries(tabsTypes.map(type => [type, 0]))
  );
  const tabsCountersKey = `${Object.values(tabsCounters)}`;

  const tabs = useMemo(() => {
    const configuredTabs = getTabs(selectConfiguredOption);

    return tabsTypes.map(type => ({
      title: $t(configuredTabs[type].title, {
        value: tabsCounters[type],
      }),
      route: getTabRoute(type),
      component: (
        <>
          <Helmet>
            <title>{$t(configuredTabs[type].documentTitle)}</title>
          </Helmet>
          <HoldsAndCheckoutsTabContent
            type={type}
            tabConfig={configuredTabs[type]}
            totalRecordsNumber={tabsCounters[type]}
            setTabsCounters={setTabsCounters}
          />
        </>
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$t, tabsCountersKey, getTabRoute, selectConfiguredOption]);

  return (
    <AddToProjectModalProvider>
      <div
        className={cn(cnBem('__container'), 'container')}
        data-auto="holds-and-checkouts-container"
      >
        <PageTitle>
          <HoldsAndCheckoutsMessage
            requestsMessage={$t(sharedMessages.requestsAndLoans)}
            holdsMessage={$t(sharedMessages.holdsAndCheckouts)}
          />
        </PageTitle>
        <TabsWithRouting
          tabs={tabs}
          parentRoute={holdsAndCheckoutsRoute}
          className={cnBem('__tabs')}
        />
      </div>
    </AddToProjectModalProvider>
  );
};
